import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {IProject} from '../Interfaces/IAllProjects';
import {listModuleInstances, listProjects} from '../BackendFacade/apiCalls';
import {GlobalDictionary} from '../../../Utils/globalDictionary';
import {IContact} from '../Interfaces/IContact';
import {GetProfileImages} from '../../../Utils/ProfilImage/ProfileImageApi';
import {Link} from 'react-router-dom';
import {useMain} from '../../../Utils/SessionControls/mainContext';
import OdxAreaHeader from '../../../OdxComponents/NativeOdx/Odx-AreaHeader';
import {odxIconSets} from '../../../OdxComponents/constants';
import OdxSearchbar from '../../../OdxComponents/NativeOdx/Odx-Searchbar';
import {transformOdxLayoutRef} from '../../../OdxComponents/odxLayoutRef';
import OdxMainFilterGroup from '../../../OdxComponents/NativeOdx/Odx-MainFilterGroup';
import AppProjectsCard from '../../../OdxComponents/CustomOdx/AllProjectsCard';
import OdxTable from '../../../OdxComponents/NativeOdx/Table/Odx-Table';
import {OdxTableCell} from '../../../OdxComponents/NativeOdx/Table/Odx-Tablecell';
import {OdxTableRow} from '../../../OdxComponents/NativeOdx/Table/Odx-TableRow';
import MailAvatar from '../../../OdxComponents/CustomOdx/MailAvatar';
import {Layout} from '../Interfaces/IHubLayout';
import AddProject from "./Modals/AddProject";
import {IModule} from "../../../BackendFacade/IModuleInstances";
import {isUserroleSufficient} from "../../../Utils/authorization";
import {UserRoles} from "../../../Utils";
import OdxSelect, {OdxSelectOptionProps} from "../../../OdxComponents/NativeOdx/FormControls/Odx-Select";
import {IListStaticModule} from "../../../BackendFacade/IStaticModules";
import OdxChip from "../../../OdxComponents/NativeOdx/Odx-Chip";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";
import {orderBy} from "../../../Utils/sorting";
import {simplifyForHtmlId} from "../../../Utils/transformer";

const AllProjects: React.FC = () => {
    const [listOfProjects, setListOfProjects] = React.useState<IProject[]>([]);
    const [filteredProjects, setFilteredProjects] = useState<IProject[]>([]);

    const {createErrorToast, setLoading, isLoading} = useMain();
    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [layout, setLayout] = React.useState<Layout>(Layout.Grid);
    const [moduleCount, setModuleCount] = React.useState<{ [key: string]: number }>({});
    const baseId = 'hub-all-projects';

    // Module filter
    const [staticModules, setStaticModules] = React.useState<IListStaticModule[]>([]);
    const [includedModules, setIncludedModules] = React.useState<OdxSelectOptionProps[]>([]);
    const [searchTerm, setSearchTerm] = React.useState<string>('');

    useModuleViewTracking('hub-allprojects')

    useEffect(() => {
        if (containerRef.current) {
            transformOdxLayoutRef(containerRef.current);
        }
    }, [containerRef, isLoading, filteredProjects]);

    useEffect(() => {
        if (containerRef.current) {
            transformOdxLayoutRef(containerRef.current);
        }
    }, [listOfProjects, layout]);

    useEffect(() => {
        if (!GlobalDictionary.get(GlobalDictionary.PROJECTS)) {
            getListOfAllProjectsWait();
        } else {
            // If the projects are already loaded, set the project list
            setProjectList(undefined).then(() => {

                // Get an update in case the projects have changed
                pullModulesAndProjects();
            })

        }
    }, []);

    // Update the filtered projects when the included modules or search term changes
    useEffect(() => {
        filterProjects();
    }, [includedModules, searchTerm]);

    const getListOfAllProjectsWait = async () => {
        setLoading(true);
        await pullModulesAndProjects();
        setLoading(false);
    };

    const filterProjects = () => {
        let projects = []
        if (searchTerm.trim() === '') {
            projects = listOfProjects;
        } else {
            projects = listOfProjects.filter((project) =>
                project.displayName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        projects = filterProjectsByModules(projects);
        sortProjects(projects);
        setFilteredProjects(projects);
    };

    const sortProjects = (projects: IProject[]) => {
        return orderBy(projects, x => x.displayName);
    }

    const pullModulesAndProjects = async () => {
        const staticModules = await GlobalDictionary.getStaticModules() as IListStaticModule[];
        setStaticModules(orderBy(staticModules, x => x.displayName));

        await listProjects().then(async (data) => {
            if (data.success) {
                const projects = data.data.value as IProject[];
                GetProfileImages(extractListOfContacts(data.data.value));
                await setProjectList(projects);
            } else {
                createErrorToast("Error loading projects: " + data.message);
            }
        });
    };

    /**
     * Get number of modules for a project
     * @param projectId
     */
    const getProjectModules = async (projectId: string) => {
        const data = await listModuleInstances(projectId);
        if (data.success) {
            let ret = data.data.value as IModule[];

            setModuleCount((prev) => {
                return {...prev, [projectId]: ret.length};
            })

            // get list of module display names
            const moduleDisplayNames = ret.map((module) => {
                return module.staticModuleId;
            });
            // filter out duplicates
            return Array.from(new Set(moduleDisplayNames));
        } else {
            createErrorToast(data.message);
            return [];
        }
    }

    const setProjectList = async (projects: IProject[]) => {
        if (!projects) {
            projects = GlobalDictionary.get(GlobalDictionary.PROJECTS) as IProject[];
        }
        sortProjects(projects);
        projects.forEach(async (project) => {
            project.moduleTypes = await getProjectModules(project.projectId);
        })
        setListOfProjects(projects);
        setFilteredProjects(projects); // Set filtered projects initially
        GlobalDictionary.set(GlobalDictionary.PROJECTS, projects);
        setLoading(false);
    };

    const extractListOfContacts = (projects: IProject[]) => {
        const listOfContacts = projects.map((item) => {
            return item.contacts;
        });

        let setOfContacts: string[] = [];
        listOfContacts.forEach((item) => {
            item.forEach((contact: IContact) => {
                setOfContacts.push(contact.mail);
            });
        });

        return Array.from(new Set(setOfContacts));
    };

    const addProject = () => {
        setModalOpen(true);
    };

    const toggleLayout = () => {
        setLayout(layout === Layout.Grid ? Layout.List : Layout.Grid);
    };

    /**
     * Filter projects by included modules
     * @param listofProjects
     */
    const filterProjectsByModules = (listofProjects: IProject[]) => {
        // filter filteredProjects by includedModules
        if (includedModules.length > 0) {
            return listofProjects.filter((project) => {
                return project.moduleTypes.some((module) => {
                    return includedModules.some((selectedModule) => {
                        return selectedModule.value === module;
                    });
                });
            });
        } else {
            return listofProjects;
        }

    }

    const getGridView = () => {
        return (
            <div id={`${baseId}-grid-wrapper`}>
                <div className={'odx-padding-top-24 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'} id={`${baseId}-grid`}>
                    {isLoading ? (
                        <></>
                    ) : filteredProjects.length > 0 ? (
                        filteredProjects
                            .map((item, index) => {
                                const projectKey = simplifyForHtmlId(item.projectId ?? `project-${index}`);
                                return (
                                    <div
                                        key={item.projectId ?? index}
                                        className={'flex flex-col h-full'}
                                        id={`${baseId}-grid-item-${projectKey}`}
                                    >
                                        <Link
                                            to={`/project/${item.projectId}`}
                                            className={"h-full"}
                                            id={`${baseId}-grid-link-${projectKey}`}
                                        >
                                            <AppProjectsCard
                                                key={index}
                                                title={item.displayName}
                                                description={item.description}
                                                imageSrc={item.customProjectIcon}
                                                numberOfModules={moduleCount[item.projectId] != undefined ? moduleCount[item.projectId] : -1}
                                                contacts={item.contacts}
                                                source={item.source}
                                                mode={'project'}
                                                forceSmallIcon={item.customProjectIcon === undefined}
                                                isAdmin={isUserroleSufficient(UserRoles.admin)}
                                                idPrefix={`${baseId}-grid-item-${projectKey}`}
                                            />
                                        </Link>
                                    </div>
                                );
                            })
                    ) : (
                        <p>Keine Projekte vorhanden. Falls ein Zugriff vorhanden sein sollte, bitte neu einloggen.</p>
                    )}
                </div>
            </div>
        );
    };

    const getListView = () => {
        return (
            <div className={'odx-padding-top-24'} id={`${baseId}-list-wrapper`}>
                <OdxTable
                    className={'pt-10 table-fixed'}
                    variant={'default'}
                    id={`${baseId}-table`}
                    headerData={[
                        {name: 'Name', title: 'Name', sortable: true},
                        {name: 'Short Description', title: 'Short Description', sortable: false},
                        {name: 'Modules Used', title: 'Modules Used', sortable: true},
                        {name: 'Contacts', title: 'Contacts', sortable: false}
                    ]}
                >
                    {isLoading ? (
                        <></>
                    ) : filteredProjects.length > 0 ? (
                        filteredProjects
                            .map((item, index) => {
                                const projectKey = simplifyForHtmlId(item.projectId ?? `project-${index}`);
                                return (
                                    <OdxTableRow key={item.projectId ?? index} id={`${baseId}-row-${projectKey}`}>
                                        <OdxTableCell id={`${baseId}-cell-${projectKey}-name`}>
                                            <Link
                                                className={"block w-full h-full no-underline content-center"}
                                                to={`/project/${item.projectId}`}
                                                id={`${baseId}-link-${projectKey}-name`}
                                            >
                                                <div className={'items-center'} id={`${baseId}-name-wrapper-${projectKey}`}>
                                                    <odx-avatar
                                                        size="xxl"
                                                        shape="circle"
                                                        variant="ghost"
                                                        className="flex items-center justify-center"
                                                        style={{"--size": "64px"} as React.CSSProperties}
                                                        key={index}
                                                        id={`${baseId}-avatar-${projectKey}`}
                                                    >
                                                        <odx-icon name="core::folder" size="xxl"></odx-icon>
                                                    </odx-avatar>
                                                    <div id={`${baseId}-name-${projectKey}`}>{item.displayName}</div>
                                                </div>
                                            </Link>
                                        </OdxTableCell>
                                        <OdxTableCell id={`${baseId}-cell-${projectKey}-description`}>
                                            <Link
                                                className={"block w-full h-full no-underline content-center"}
                                                to={`/project/${item.projectId}`}
                                                id={`${baseId}-link-${projectKey}-description`}
                                            >
                                                {item.description}
                                            </Link>
                                        </OdxTableCell>
                                        <OdxTableCell id={`${baseId}-cell-${projectKey}-modules`}>
                                            <Link
                                                className={"block w-full h-full no-underline content-center"}
                                                to={`/project/${item.projectId}`}
                                                id={`${baseId}-link-${projectKey}-modules`}
                                            >
                                                {moduleCount[item.projectId] != undefined ? moduleCount[item.projectId] : "Loading..."}
                                            </Link>
                                        </OdxTableCell>
                                        <OdxTableCell id={`${baseId}-cell-${projectKey}-contacts`}>
                                            <Link
                                                className={"block w-full h-full no-underline content-center"}
                                                to={`/project/${item.projectId}`}
                                                id={`${baseId}-link-${projectKey}-contacts`}
                                            >
                                                {item.contacts.map((contact, c_index) => (
                                                    <MailAvatar key={index * 100 + c_index} size={'small'} mail={contact.mail}
                                                                variant={'circle'}
                                                                id={`${baseId}-contact-${projectKey}-${simplifyForHtmlId(contact.mail ?? `contact-${c_index}`)}`}
                                                    />
                                                ))}
                                            </Link>
                                        </OdxTableCell>
                                    </OdxTableRow>
                                );
                            })
                    ) : (
                        <p>Keine Projekte vorhanden. Falls ein Zugriff vorhanden sein sollte, bitte neu einloggen.</p>
                    )}
                </OdxTable>
            </div>
        );
    };

    const getStaticModuleOptions = (): OdxSelectOptionProps[] => {
        return staticModules.map((module) => ({
            value: module.staticModuleId,
            label: module.displayName,
        }));
    }

    return (
        <>
            <div ref={containerRef} id={`${baseId}-view`}>
                <AddProject
                    isOpen={modalOpen}
                    closeModal={() => setModalOpen(false)}
                    triggerReload={() => getListOfAllProjectsWait()}
                />

                <OdxAreaHeader
                    odxSize={'large'}
                    text={'All Projects'}
                    odxContent={
                    <>
                        {isUserroleSufficient(UserRoles.admin) && (
                            <odx-button
                                variant="accent"
                                id={`OdxButton_${baseId}-add-project-btn`}
                                type="button"
                                onClick={addProject}
                            >
                                <odx-icon slot="prefix" name={`${odxIconSets.core}::plus`} size="md"></odx-icon>
                                Add new project
                            </odx-button>
                        )}
                    </>
                    }
                    className={'!pl-0 !pr-0'}
                />

                <div>
                    <div className={"flex items-center"}>
                        <div className={"flex-grow"}>
                            <OdxSearchbar onSearch={setSearchTerm} id={`${baseId}-search`}/>
                        </div>
                        <div className={"!ml-2 flex h-fit"}>
                            <OdxChip size={"small"} content={filteredProjects.length.toString()} id={`${baseId}-project-count`}/>
                            <div className={"!ml-2"} id={`${baseId}-project-count-label`}>
                                {filteredProjects.length === 1 ? 'Project' : 'Projects'} found
                            </div>
                        </div>
                    </div>

                    <div></div>
                    <div className={'justify-self-end'}>
                        <OdxMainFilterGroup>
                            <OdxSelect
                                id={`${baseId}-module-filter`}
                                isMulti
                                options={getStaticModuleOptions()}
                                value={includedModules.map(option => option.value)}
                                label={"Filter by Module"}
                                onChange={(selectedValues: string[]) => {
                                    const updatedIncludedModules = selectedValues.map(value => ({
                                        value,
                                        label: staticModules.find(module => module.staticModuleId === value)?.displayName || '',
                                    }));
                                    setIncludedModules(updatedIncludedModules);
                                }}
                                placeholder={"Include Modules"}
                                matchReferenceWidth={false}
                                showMultiOptions={true}
                            />
                            <odx-icon-button
                                variant="ghost"
                                type="button"
                                icon={`${odxIconSets.core}::${Layout.Grid === layout ? 'dashboard' : 'menu'}`}
                                onClick={toggleLayout}
                                id={`OdxButton_${baseId}-layout-toggle`}
                            />
                        </OdxMainFilterGroup>
                    </div>
                </div>
                {layout === Layout.Grid ? getGridView() : getListView()}
            </div>
        </>
    );
}

export default AllProjects;
